import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, X, ChevronRight, ThumbsUp, ThumbsDown, Flag, Rocket, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { useGuard } from "@/lib/guards";
import { resolveMediaUrl } from "@/components/FileUpload";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { ListingPrice } from "@/components/marketplace/Promo";
import { ReportDialog } from "@/components/ReportDialog";

// Lot 791 — Marketplace dans le fil, à la Facebook : « Ça m'intéresse » (favori + plus d'annonces de ce genre),
// « Pas intéressé » (annonce cachée pour moi, annulable), « Signaler » (file À traiter). Une carte intelligente
// « Marketplace pour toi » : UNE annonce mise en avant par son vendeur (boost ou promo), choisie d'après mes goûts,
// présentée comme une publication. Garde-fous : market_feed_card, listing_interest, listing_report.
const GREEN = "#1B7A3D";
const GREEN_SOFT = "#E7F6EC";

const useFeedList = (path, enabled) => {
  const [data, setData] = useState({ listings: [], personalized: false });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!enabled) { setData({ listings: [], personalized: false }); setLoaded(true); return undefined; }
    let alive = true;
    const cancel = whenFeedReady(() => {
      api.get(path).then(({ data: d }) => { if (alive) setData({ listings: d.listings || [], personalized: !!d.personalized }); })
        .catch(() => {}).finally(() => { if (alive) setLoaded(true); });
    });
    return () => { alive = false; cancel(); };
  }, [enabled, path]);
  return { ...data, loaded };
};
export const useMarketForYou = (enabled = true) => useFeedList("/marketplace/for-you?limit=1", enabled);

// Barre Ça m'intéresse / Pas intéressé / Signaler. `onHidden(it, reason)` : le parent retire l'annonce de l'écran ;
// `onInterested(it, on)` : le parent met à jour favori / état. Annuler (toast) remet l'annonce comme avant.
export const ListingInterestBar = ({ it, onHidden, onInterested, onRestore, compact = false, testPrefix = "listing" }) => {
  const { t, user } = useApp();
  const interestOn = useGuard("listing_interest");
  const reportOn = useGuard("listing_report");
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  if (!user || it?.is_mine || (user && it?.seller?.id === user.id) || (!interestOn && !reportOn)) return null;
  const interested = it.interest === true || (it.interest !== false && !!it.is_favorite);
  const stop = (e) => { e?.stopPropagation?.(); e?.preventDefault?.(); };

  const undo = async (reason) => {
    try {
      await api.delete(`/marketplace/listings/${it.id}/interest`);
      onRestore?.(it, reason);
    } catch { toast.error(t("common.error", "Erreur")); }
  };
  const setInterest = async (e, on) => {
    stop(e);
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/marketplace/listings/${it.id}/interest`, { interested: on });
      if (on) {
        onInterested?.(it, true, data);
        toast.success(t("market.interested_done", "Noté ! Tu verras plus d'annonces de ce genre, et celle-ci est dans tes favoris."));
      } else {
        onHidden?.(it, "not_interested");
        toast(t("market.not_interested_done", "Annonce masquée. Tu ne la reverras plus."), { action: { label: t("market.undo", "Annuler"), onClick: () => undo("not_interested") } });
      }
    } catch (err) { toast.error(err?.friendlyMessage || t("common.error", "Erreur")); }
    setBusy(false);
  };
  const report = async (reason, category) => {
    setReporting(true);
    try {
      await api.post(`/marketplace/listings/${it.id}/report`, { reason, category });
      setReportOpen(false);
      onHidden?.(it, "reported");
      toast.success(t("market.report_done", "Merci, l'annonce a été signalée à l'équipe et masquée pour toi."));
    } catch (err) { toast.error(err?.friendlyMessage || t("common.error", "Erreur")); }
    setReporting(false);
  };
  const btn = `inline-flex items-center gap-1 rounded-full font-semibold transition-colors disabled:opacity-60 ${compact ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs"}`;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" onClick={stop} data-testid={`${testPrefix}-interest-bar-${it.id}`}>
      {interestOn && (
        <>
          <button type="button" disabled={busy || interested} onClick={(e) => setInterest(e, true)} className={btn}
            style={interested ? { background: GREEN, color: "#fff" } : { background: GREEN_SOFT, color: GREEN }} aria-pressed={interested} data-testid={`${testPrefix}-interested-${it.id}`}>
            <ThumbsUp size={compact ? 12 : 14} /> {interested ? t("market.interested_on", "Ça t'intéresse") : t("market.interested", "Ça m'intéresse")}
          </button>
          <button type="button" disabled={busy} onClick={(e) => setInterest(e, false)} className={btn} style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }} data-testid={`${testPrefix}-not-interested-${it.id}`}>
            <ThumbsDown size={compact ? 12 : 14} /> {t("market.not_interested", "Pas intéressé")}
          </button>
        </>
      )}
      {reportOn && (
        <button type="button" onClick={(e) => { stop(e); setReportOpen(true); }} className={btn} style={{ color: "var(--yalola-text-muted)" }} title={t("market.report", "Signaler")} data-testid={`${testPrefix}-report-${it.id}`}>
          <Flag size={compact ? 12 : 14} /> {!compact && t("market.report", "Signaler")}
        </button>
      )}
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} onReport={report} reporting={reporting} testidPrefix={`${testPrefix}-report`} title={t("market.report_title", "Signaler cette annonce")} />
    </div>
  );
};

const Dismiss = ({ onDismiss, t, testId }) => onDismiss && (
  <button onClick={(e) => { e.stopPropagation(); onDismiss(); }} className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full hover:bg-[rgba(0,0,0,0.06)] z-10" aria-label={t("common.close", "Fermer")} data-testid={testId}>
    <X size={14} />
  </button>
);

// L'annonce de `data` moins celle cachée pendant la session de la carte (Pas intéressé / Signaler), avec ses retouches.
const useVisible = (listings) => {
  const [hidden, setHidden] = useState({});
  const [patched, setPatched] = useState({});
  const visible = (listings || []).filter((l) => !hidden[l.id]).map((l) => ({ ...l, ...(patched[l.id] || {}) }));
  const hide = (it) => setHidden((h) => ({ ...h, [it.id]: true }));
  const restore = (it) => setHidden((h) => { const n = { ...h }; delete n[it.id]; return n; });
  const patch = (it, p) => setPatched((m) => ({ ...m, [it.id]: { ...(m[it.id] || {}), ...p } }));
  return { visible, hide, restore, patch };
};

// « Marketplace pour toi » : une annonce mise en avant par son vendeur, présentée comme une publication
// (vendeur, photo, prix, actions). Quand le membre la refuse / la signale, la carte dit merci et renvoie vers la Marketplace.
export const MarketForYouCard = ({ data, onDismiss = null }) => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const { visible, hide, restore, patch } = useVisible(data?.listings);
  const it = visible[0];
  if (!data?.listings?.length) return null;
  const kicker = (
    <div className="text-[11px] inline-flex items-center gap-1 flex-wrap" style={{ color: GREEN }} data-testid="market-for-you-label">
      <ShoppingBag size={11} /> <b>{t("market.for_you", "Marketplace pour toi")}</b>
      <span style={{ color: "var(--yalola-text-muted)" }}>· {data.personalized ? t("market.for_you_personal", "Choisie d'après tes favoris et tes visites") : t("market.boosted_feed_hint", "Le vendeur a mis cette annonce en avant")}</span>
    </div>
  );
  if (!it) {
    return (
      <div className="yalola-card p-4 relative space-y-2" data-testid="market-for-you-card" data-state="hidden">
        <Dismiss onDismiss={onDismiss} t={t} testId="market-for-you-dismiss" />
        {kicker}
        <div className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="market-for-you-empty">{t("market.for_you_all_hidden", "Merci ! Tes choix affinent les prochaines propositions.")}</div>
        <button onClick={() => nav("/marketplace")} className="text-xs font-semibold inline-flex items-center gap-0.5 hover:underline" style={{ color: GREEN }} data-testid="market-for-you-all">
          {t("market.see_marketplace", "Voir la Marketplace")} <ChevronRight size={14} />
        </button>
      </div>
    );
  }
  const open = () => nav(`/marketplace?item=${encodeURIComponent(it.id)}&from=feed`);
  return (
    <article className="yalola-card overflow-hidden relative" data-testid="market-for-you-card" data-listing-id={it.id}>
      <Dismiss onDismiss={onDismiss} t={t} testId="market-for-you-dismiss" />
      <div className="flex items-center gap-2.5 p-4 pb-3 pr-10">
        {it.seller && <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(it.seller, 80)} /><AvatarFallback>{it.seller.name?.[0]}</AvatarFallback></Avatar>}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{it.seller?.name}</div>
          {kicker}
        </div>
      </div>
      <div role="link" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }} className="cursor-pointer" data-testid={`market-for-you-item-${it.id}`}>
        {it.images?.[0]
          ? <img src={resolveMediaUrl(it.images[0])} alt="" className="w-full max-h-[420px] aspect-[4/3] object-cover bg-black" />
          : <div className="w-full aspect-[4/3] grid place-items-center" style={{ background: "var(--yalola-surface-alt)" }}><ShoppingBag size={40} className="opacity-30" /></div>}
        <div className="p-4 pb-2 space-y-1">
          <div className="font-semibold text-base line-clamp-2" data-testid="market-for-you-title">{it.title}</div>
          <ListingPrice it={it} t={t} lang={lang} />
          <div className="text-xs flex items-center gap-2 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
            {it.boosted && <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="market-for-you-boosted"><Rocket size={11} /> {t("market.boosted_feed", "Annonce mise en avant")}</span>}
            {it.city && <span className="inline-flex items-center gap-0.5"><MapPin size={11} /> {it.city}</span>}
            <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: GREEN }} data-testid="market-for-you-open">{t("market.see_listing", "Voir l'annonce")} <ChevronRight size={13} /></span>
          </div>
        </div>
      </div>
      <div className="px-4 pb-4 pt-1">
        <ListingInterestBar it={it} testPrefix="mfy" onHidden={hide} onRestore={restore} onInterested={(x) => patch(x, { interest: true, is_favorite: true })} />
      </div>
    </article>
  );
};
